import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { can, MIN_REASON } from '@urbanrats/ops-core';
import type { PipelineCamera, PipelineView } from '../backend/types.ts';
import { DataTable } from '../components/DataTable.tsx';
import { NotMeasured, PageHead, Section, StateTag } from '../components/common.tsx';
import { fmtTime, useCtx } from '../lib.ts';

const POOL_STATES = ['ACTIVE', 'STANDBY', 'QUALIFIED', 'DEGRADED', 'OFFLINE', 'RIGHTS_REVIEW', 'DISABLED'];
const mb = (b: number) => `${Math.round(b / 1048576)} MB`; // MB as configured (URBANRATS_INGEST_DISK_BUDGET_MB = MiB)
const ago = (iso: string | null, now: number) => (iso ? `${Math.max(0, Math.round((now - Date.parse(iso)) / 1000))} s ago` : null);

/**
 * Camera pipeline (backend only): pool counts against targets, open alerts, ingest sessions, round media
 * locks, live round outcomes, incidents, and per-camera operator actions (reason required, audited
 * server-side). Refreshes every 15 s. Every number is a count of the rows listed on this page.
 */
export function CameraPipeline() {
  const { backend, user } = useCtx();
  const ops = backend.cameraOps;
  const [view, setView] = useState<PipelineView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const reload = useCallback(() => setVersion((n) => n + 1), []);
  useEffect(() => {
    if (!ops) return;
    let live = true;
    const load = () =>
      void ops.get().then((r) => {
        if (!live) return;
        if (r.ok) {
          setView(r.value);
          setErr(null);
        } else setErr(r.error);
      });
    load();
    const t = setInterval(load, 15_000);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, [ops, version]);

  if (!ops)
    return (
      <>
        <PageHead title="Camera pipeline">Pools, ingest, locked round media and alerts live on the backend.</PageHead>
        <p className="empty" data-testid="pipeline-demo-note">
          This page needs the UrbanRats backend (npm run api:dev and the admin in API mode). The demo has no camera pipeline, so nothing here is simulated.
        </p>
      </>
    );
  if (err && !view) return <p role="alert" className="field-error">{err}</p>;
  if (!view) return <p aria-busy="true">Loading…</p>;
  const s = view.snapshot;
  const now = Date.parse(s.generatedAt);
  const rows = view.cameras.filter((c) => !filter || c.state === filter || (filter === 'HELD' && c.hold_until && Date.parse(c.hold_until) > now));
  const cam = view.cameras.find((c) => c.camera_id === selected) ?? null;
  const target = (st: string) => (st === 'ACTIVE' ? s.targets.active : st === 'STANDBY' ? s.targets.standby : null);
  return (
    <>
      <PageHead title="Camera pipeline">
        {s.mode} · pool profile {s.profile} · health job {s.jobs.health ? 'on' : 'off'} · pool manager {s.jobs.pools ? 'on' : 'off'} · as of {fmtTime(s.generatedAt)}
        {err && <span className="field-error"> · refresh failed: {err}</span>}
      </PageHead>

      <Section title="Alerts" testId="pipeline-alerts">
        {view.alerts.length === 0 ? (
          <p className="muted">No open alerts.</p>
        ) : (
          <ul className="alert-list">
            {view.alerts.map((a) => (
              <li key={a.code} className={`alert sev-${a.severity}`} data-testid={`alert-${a.code}`}>
                <StateTag state={a.severity} /> <strong>{a.code}</strong> {a.message}
                {a.refs.length > 0 && (
                  <>
                    {' '}
                    ·{' '}
                    {a.refs.map((r) => (
                      <button key={r} type="button" className="linkish" onClick={() => setSelected(r)}>
                        {r}
                      </button>
                    ))}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Pools" testId="pipeline-pools">
        <p className="note">
          {s.cameras.discovered.toLocaleString('en')} cameras discovered, {s.cameras.eligible.toLocaleString('en')} technically eligible (https HLS traffic streams). Providers by rights:{' '}
          {Object.entries(s.providers)
            .map(([k, n]) => `${k} ${n}`)
            .join(' · ')}
          . {s.roundReady} ACTIVE camera(s) can host the next live round (fresh ingest, calibration, not held).
        </p>
        <div className="chips" role="group" aria-label="Filter cameras by state">
          <button type="button" className={`chip ${filter === null ? 'on' : ''}`} onClick={() => setFilter(null)}>
            all listed ({view.cameras.length})
          </button>
          {POOL_STATES.map((st) => (
            <button key={st} type="button" className={`chip ${filter === st ? 'on' : ''}`} onClick={() => setFilter(st)} data-testid={`pool-${st}`}>
              {st} {s.cameras.byState[st] ?? 0}
              {target(st) !== null && ` / ${target(st)}`}
            </button>
          ))}
          <button type="button" className={`chip ${filter === 'HELD' ? 'on' : ''}`} onClick={() => setFilter('HELD')} data-testid="pool-HELD">
            held {s.cameras.held}
          </button>
        </div>
        <DataTable<PipelineCamera>
          testId="pipeline-cameras"
          rows={rows}
          rowKey={(c) => c.camera_id}
          empty="No cameras in this state."
          exportName="camera-pipeline"
          columns={[
            {
              key: 'cam',
              header: 'Camera',
              cell: (c) => (
                <button type="button" className="linkish" onClick={() => setSelected(c.camera_id)} data-testid={`cam-${c.camera_id}`}>
                  {c.name ?? c.camera_id}
                </button>
              ),
              csv: (c) => c.camera_id,
            },
            { key: 'state', header: 'State', cell: (c) => <><StateTag state={c.state} />{c.pinned && ' 📌'}{c.hold_until && Date.parse(c.hold_until) > now && <span className="muted"> held</span>}</>, csv: (c) => c.state },
            { key: 'where', header: 'Location', cell: (c) => [c.road, c.city, c.country].filter(Boolean).join(', ') || '—', csv: (c) => [c.road, c.city, c.country].filter(Boolean).join(', ') },
            { key: 'provider', header: 'Provider · rights', cell: (c) => <>{c.provider_id} · <StateTag state={c.rights_status} /></>, csv: (c) => `${c.provider_id} ${c.rights_status}` },
            { key: 'video', header: 'Video', cell: (c) => (c.resolution ? `${c.resolution}${c.fps ? ` @ ${Math.round(c.fps)}` : ''}` : <NotMeasured />), csv: (c) => c.resolution ?? '' },
            { key: 'health', header: 'Health · uptime', cell: (c) => `${c.health_status} · ${c.checks_total ? Math.round((100 * c.checks_ok) / c.checks_total) : 0} % of ${c.checks_total}`, csv: (c) => `${c.health_status} ${c.checks_ok}/${c.checks_total}` },
            { key: 'score', header: 'Score', cell: (c) => (c.score === null ? <NotMeasured /> : c.score.toFixed(1)), csv: (c) => c.score ?? '' },
            { key: 'act', header: 'Vehicles/frame', cell: (c) => (c.vehicles_mean === null ? <NotMeasured /> : c.vehicles_mean.toFixed(1)), csv: (c) => c.vehicles_mean ?? '' },
            { key: 'ingest', header: 'Last ingest', cell: (c) => (c.last_ingest_at ? `${c.ingest_status} · ${ago(c.last_ingest_at, now)}` : '—'), csv: (c) => c.last_ingest_at ?? '' },
            { key: 'inc', header: 'Incidents 24 h', cell: (c) => c.incidents_24h, csv: (c) => c.incidents_24h },
          ]}
        />
      </Section>

      {cam && <CameraPanel cam={cam} now={now} mayManage={can(user, 'camera.manage')} onDone={reload} onClose={() => setSelected(null)} />}

      <Section title="Ingest" testId="pipeline-ingest">
        <p className="note">
          Rolling buffer {mb(s.ingest.bufferBytes)} of {mb(s.limits.diskBudgetBytes)} · at most {s.limits.maxIngestSessions} sessions · stall after {s.limits.stallMs / 1000} s without a segment
        </p>
        <DataTable
          rows={s.ingest.sessions}
          rowKey={(x) => x.cameraId}
          empty="No ingest sessions (is the camera-ingest process running?)."
          columns={[
            { key: 'c', header: 'Camera', cell: (x) => x.cameraId },
            { key: 's', header: 'Status', cell: (x) => <StateTag state={x.status} /> },
            { key: 'w', header: 'Worker', cell: (x) => x.workerId },
            { key: 'h', header: 'Heartbeat', cell: (x) => `${x.heartbeatAgeS} s ago` },
            { key: 'l', header: 'Last segment', cell: (x) => (x.lastSegmentAgeS === null ? '—' : `${x.lastSegmentAgeS} s ago`) },
            { key: 'n', header: 'Segments · gaps', cell: (x) => `${x.segments} · ${x.gaps}` },
            { key: 'e', header: 'Last error', cell: (x) => x.lastError ?? '—' },
          ]}
        />
      </Section>

      <Section title="Round media and live rounds (24 h)" testId="pipeline-rounds">
        <dl className="kv">
          <dt>Locked assets</dt>
          <dd>
            {Object.entries(s.roundMedia24h.byStatus)
              .map(([k, n]) => `${k} ${n}`)
              .join(' · ') || 'none'}
            {s.roundMedia24h.lockSecondsP50 !== null && ` · window end → READY p50 ${s.roundMedia24h.lockSecondsP50.toFixed(1)} s, p95 ${s.roundMedia24h.lockSecondsP95!.toFixed(1)} s`}
          </dd>
          <dt>Rejection reasons</dt>
          <dd>{s.roundMedia24h.reasons.map((r) => `${r.n}× ${r.reason}`).join(' | ') || '—'}</dd>
          <dt>Live rounds</dt>
          <dd>
            {Object.entries(s.liveRounds24h.byState)
              .map(([k, n]) => `${k} ${n}`)
              .join(' · ') || 'none'}
          </dd>
          <dt>Void reasons</dt>
          <dd>{s.liveRounds24h.voidReasons.map((r) => `${r.n}× ${r.reason}`).join(' | ') || '—'}</dd>
          <dt>Health checks (1 h)</dt>
          <dd>
            {s.health.okLastHour} ok of {s.health.checksLastHour} · last {s.health.lastCheckAt ? fmtTime(s.health.lastCheckAt) : 'never'}
          </dd>
        </dl>
      </Section>

      <Section title="Incidents" testId="pipeline-incidents">
        <DataTable
          rows={s.recentIncidents}
          rowKey={(x) => `${x.cameraId}-${x.at}-${x.kind}`}
          empty="No incidents recorded."
          columns={[
            { key: 'at', header: 'At', cell: (x) => fmtTime(x.at) },
            { key: 'c', header: 'Camera', cell: (x) => x.cameraId },
            { key: 'k', header: 'Kind', cell: (x) => <StateTag state={x.kind} /> },
            { key: 'd', header: 'Detail', cell: (x) => x.detail },
            { key: 'r', header: 'Round', cell: (x) => x.gameRoundId ?? '—' },
          ]}
        />
      </Section>

      <Section title="State changes" testId="pipeline-events">
        <DataTable
          rows={view.events}
          rowKey={(x) => `${x.camera_id}-${x.at}-${x.to_state}`}
          empty="No state changes yet."
          columns={[
            { key: 'at', header: 'At', cell: (x) => fmtTime(x.at) },
            { key: 'c', header: 'Camera', cell: (x) => x.camera_id },
            { key: 't', header: 'Change', cell: (x) => `${x.from_state} → ${x.to_state}` },
            { key: 'r', header: 'Reason', cell: (x) => x.reason },
            { key: 'a', header: 'By', cell: (x) => x.actor },
          ]}
        />
      </Section>
    </>
  );
}

function CameraPanel({ cam, now, mayManage, onDone, onClose }: { cam: PipelineCamera; now: number; mayManage: boolean; onDone: () => void; onClose: () => void }) {
  const { backend } = useCtx();
  const ops = backend.cameraOps!;
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const id = useId();
  const ok = reason.trim().length >= MIN_REASON;
  const run = async (f: () => Promise<{ ok: true; value: string } | { ok: false; error: string }>) => {
    setBusy(true);
    const r = await f();
    setBusy(false);
    setMsg(r.ok ? { ok: true, text: r.value } : { ok: false, text: r.error });
    if (r.ok) onDone();
  };
  // Only actions the server accepts from this state are offered (no dead buttons).
  const actions: { action: 'activate' | 'standby' | 'deactivate' | 'disable' | 'enable'; label: string; when: boolean }[] = [
    { action: 'activate', label: 'Activate (pin)', when: cam.health_status === 'OK' && !['ACTIVE', 'DISABLED', 'RIGHTS_REVIEW'].includes(cam.state) },
    { action: 'standby', label: 'Move to standby', when: ['ACTIVE', 'QUALIFIED'].includes(cam.state) },
    { action: 'deactivate', label: 'Deactivate (hold 24 h)', when: !['DISABLED', 'RIGHTS_REVIEW'].includes(cam.state) },
    { action: 'disable', label: 'Disable', when: cam.state !== 'DISABLED' },
    { action: 'enable', label: 'Enable', when: ['DISABLED', 'RIGHTS_REVIEW'].includes(cam.state) },
  ];
  return (
    <Section title={`Camera ${cam.name ?? cam.camera_id}`} testId="pipeline-camera">
      <button type="button" className="btn" onClick={onClose}>
        Close
      </button>
      <dl className="kv">
        <dt>Id · provider</dt>
        <dd>
          {cam.camera_id} · {cam.provider_id} (rights <StateTag state={cam.rights_status} />)
        </dd>
        <dt>State</dt>
        <dd>
          <StateTag state={cam.state} /> {cam.state_reason}
          {cam.pinned && ' · pinned by an operator'}
        </dd>
        <dt>Hold</dt>
        <dd data-testid="camera-hold">{cam.hold_until && Date.parse(cam.hold_until) > now ? `until ${fmtTime(cam.hold_until)} — ${cam.hold_reason}` : 'none'}</dd>
        <dt>Location</dt>
        <dd>
          {[cam.road, cam.city, cam.country].filter(Boolean).join(', ') || '—'} · {cam.latitude?.toFixed(4) ?? '?'}, {cam.longitude?.toFixed(4) ?? '?'}
        </dd>
        <dt>Stream</dt>
        <dd>
          {cam.protocol} · {cam.resolution ?? 'resolution not measured'} · {cam.fps ? `${cam.fps.toFixed(1)} fps` : 'fps not measured'}
        </dd>
        <dt>Health</dt>
        <dd>
          {cam.health_status} · {cam.checks_ok}/{cam.checks_total} checks ok · last ok {cam.last_ok_at ? fmtTime(cam.last_ok_at) : 'never'}
        </dd>
        <dt>Last error</dt>
        <dd>{cam.last_ingest_error ?? cam.last_error ?? '—'}</dd>
        <dt>Calibration</dt>
        <dd>{cam.calibration_status ?? 'none (cannot host live rounds)'}</dd>
      </dl>
      {mayManage ? (
        <div className="form">
          <label htmlFor={id}>Reason (min {MIN_REASON} chars, audited)</label>
          <input id={id} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="camera-reason" />
          <div className="row">
            {actions
              .filter((a) => a.when)
              .map((a) => (
                <button key={a.action} type="button" className="btn" disabled={!ok || busy} onClick={() => void run(() => ops.action(cam.camera_id, a.action, reason.trim()))} data-testid={`camera-${a.action}`}>
                  {a.label}
                </button>
              ))}
            <button type="button" className="btn" disabled={busy} onClick={() => void run(() => ops.check(cam.camera_id))} data-testid="camera-check">
              Force health check
            </button>
            {cam.latest_asset && (
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={() =>
                  void ops.preview(cam.latest_asset!).then((r) => {
                    if (r.ok) setPreview(r.value);
                    else setMsg({ ok: false, text: r.error });
                  })
                }
                data-testid="camera-preview"
              >
                Preview last locked media
              </button>
            )}
          </div>
        </div>
      ) : (
        <p className="note">Your role can view cameras but not change them.</p>
      )}
      {msg && (
        <p role={msg.ok ? 'status' : 'alert'} className={msg.ok ? 'note' : 'field-error'} data-testid="camera-msg">
          {msg.text}
        </p>
      )}
      {preview && <HlsPreview url={preview} />}
    </Section>
  );
}

/** Our own locked round media (signed, short-lived playlist). The camera's upstream URL is never used here. */
function HlsPreview({ url }: { url: string }) {
  const v = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = v.current;
    if (!el) return;
    let h: import('hls.js').default | null = null;
    let dead = false;
    void import('hls.js').then(({ default: Hls }) => {
      if (dead) return;
      if (!Hls.isSupported()) {
        el.src = url;
        return;
      }
      h = new Hls();
      h.loadSource(url);
      h.attachMedia(el);
    });
    return () => {
      dead = true;
      h?.destroy();
    };
  }, [url]);
  return <video ref={v} controls muted playsInline className="preview-video" data-testid="camera-preview-video" />;
}
